import "./App.css";
import "@google/model-viewer/dist/model-viewer.min.js";
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import ProductList from "./components/ProductList/ProductList";
import About from "./components/About/About";
import ErrorPage from "./components/ErrorPage/ErrorPage";
import Contact from "./components/Contact/Contact";
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
import SignUp from "./components/SignUp/SignUp";
import SignIn from "./components/SignIn/Signin";
import WishList from "./components/Wishlist/WishList";
import AIAssistant from "./components/AIAssistant/AIAssistant";
import { useState, useEffect } from "react";
import ProductItems from "./data/ProductItems";
import ProductDetail from "./components/ProductDetail/ProductDetail";

// ✅ AI Features
import AISmartSearch from "./components/Aismartsearch/Aismartsearch";
import VisualSearch from "./components/Visualsearch/Visualsearch";
import AIProductComparison from "./components/AIProductComparison/AIProductComparison";
import VoiceShoppingAssistant from "./components/VoiceShoppingAssistant/VoiceShoppingAssistant";

import Checkout from "./components/Checkout/Checkout";
import OrderSuccess from "./components/OrderSuccess/OrderSuccess";
import OrderHistory from "./components/OrderHistory/OrderHistory";
import UserProfile from "./components/UserProfile/UserProfile";
import GeminiDeepThinkAdvisor from './components/GeminiDeepThinkAdvisor/GeminiDeepThinkAdvisor';

// ✅ Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('userDetails');
  return isAuthenticated ? children : <Navigate to="/sign-in" replace />;
};

const App = () => {
  const [wishlist, setWishlist] = useState([]);
  const [highlightedProduct, setHighlightedProduct] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // ✅ Check authentication status
  useEffect(() => {
    const user = localStorage.getItem('userDetails');
    setIsAuthenticated(!!user);
  }, []);

  // ✅ Listen for auth changes
  useEffect(() => {
    const handleStorageChange = () => {
      const user = localStorage.getItem('userDetails');
      setIsAuthenticated(!!user);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const addToWishlist = (item) => {
    setWishlist([...wishlist, item]);
  };

  const handleRemoveItem = (id) => {
    const updatedWishlist = wishlist.filter((item) => item.id !== id);
    setWishlist(updatedWishlist);
  };

  const handleProductRecommendation = (product) => {
    setHighlightedProduct(product);
    setTimeout(() => {
      setHighlightedProduct(null);
    }, 3000);
  };

  return (
    <>
      <BrowserRouter>
        <Header isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} />
        <Routes>
          <Route
            path="/"
            element={
              <ProductList
                addToWishlist={addToWishlist}
                wishlist={wishlist}
                removeFromWishlist={handleRemoveItem}
                highlightedProduct={highlightedProduct}
              />
            }
          />

          {/* ✅ Protected AI Features Routes - Only for logged-in users */}
          <Route
            path="/ai-search"
            element={
              <ProtectedRoute>
                <AISmartSearch
                  products={ProductItems}
                  onProductRecommendation={handleProductRecommendation}
                />
              </ProtectedRoute>
            }
          />

          <Route
            path="/visual-search"
            element={
              <ProtectedRoute>
                <VisualSearch products={ProductItems} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/compare"
            element={
              <ProtectedRoute>
                <AIProductComparison products={ProductItems} />
              </ProtectedRoute>
            }
          />

          {/* ✅ Protected Wishlist Route */}
          <Route
            path="/wishlist"
            element={
              <ProtectedRoute>
                <WishList wishlist={wishlist} removeItem={handleRemoveItem} />
              </ProtectedRoute>
            }
          />

          {/* ✅ Checkout & Order Routes - Protected */}
          <Route
            path="/checkout"
            element={
              <ProtectedRoute>
                <Checkout />
              </ProtectedRoute>
            }
          />

          <Route
            path="/order-success"
            element={
              <ProtectedRoute>
                <OrderSuccess />
              </ProtectedRoute>
            }
          />

          <Route
            path="/order-history"
            element={
              <ProtectedRoute>
                <OrderHistory />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            }
          />

          <Route path="/deep-think-advisor" element={
            <ProtectedRoute>
              <GeminiDeepThinkAdvisor />
            </ProtectedRoute>
          } />

          {/* Public Routes */}
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route
            path="/product/:productId"
            element={
              <ProductDetail
                addToWishlist={addToWishlist}
                wishlist={wishlist}
                removeFromWishlist={handleRemoveItem}
              />
            }
          />

          {/* Auth Routes */}
          <Route path="/sign-up" element={<SignUp />} />
          <Route
            path="/sign-in"
            element={<SignIn setIsAuthenticated={setIsAuthenticated} />}
          />

          {/* Error Page */}
          <Route path="*" element={<ErrorPage />} />
        </Routes>

        {/* ✅ MODIFIED - AI Assistant - Only visible after login */}
        {isAuthenticated && (
          <>
            <AIAssistant
              products={ProductItems}
              onProductRecommend={handleProductRecommendation}
            />
            {/* ✅ NEW: Voice Shopping Assistant */}
            <VoiceShoppingAssistant products={ProductItems} />
          </>
        )}
        <Footer />
      </BrowserRouter>
    </>
  );
};

export default App;
